import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import productsAPI from '../api/products';
import ordersAPI from '../api/orders';
import purchaseOrdersAPI from '../api/po';
import suppliersAPI from '../api/suppliers';
import { computeSkuRisk } from '../operational-intelligence/inventory/skuRisk';
import { computePOEscalation } from '../operational-intelligence/procurement/poEscalation';
import { computeOrderFulfillment } from '../operational-intelligence/fulfillment/orderFulfillment';
import { computeSupplierRisk } from '../operational-intelligence/suppliers/supplierRisk';

// Levels that constitute "requires attention" for each domain
const ESCALATED_SKU = new Set(['stock-out', 'critical', 'low']);
const ESCALATED_PO = new Set(['overdue', 'stale-approval', 'awaiting-approval']);
const ESCALATED_ORDER = new Set(['stale-processing', 'shipment-overdue', 'unconfirmed']);
const ESCALATED_SUPPLIER = new Set(['unreliable', 'degraded']);

const STALE_MS = 2 * 60 * 1000;    // 2 min — fresh enough for nav counts
const REFETCH_MS = 5 * 60 * 1000;  // 5 min — background refresh

/**
 * Derives escalation counts for sidebar nav badges.
 * All classification is delegated to the OI layer — this hook only fetches
 * and counts. Returns { products, purchaseOrders, orders, suppliers } as integers.
 * Falls back to 0 on error; never throws to the caller.
 */
export function useEscalationCounts() {
  const { data: productData } = useQuery({
    queryKey: ['nav-escalation-products'],
    queryFn: async () => {
      const res = await productsAPI.getAll({ limit: 500 });
      return res.data;
    },
    staleTime: STALE_MS,
    refetchInterval: REFETCH_MS,
    retry: 1,
  });

  const { data: poData } = useQuery({
    queryKey: ['nav-escalation-pos'],
    queryFn: async () => {
      const res = await purchaseOrdersAPI.getAll({ limit: 500 });
      return res.data;
    },
    staleTime: STALE_MS,
    refetchInterval: REFETCH_MS,
    retry: 1,
  });

  const { data: orderData } = useQuery({
    queryKey: ['nav-escalation-orders'],
    queryFn: async () => {
      const res = await ordersAPI.getAll({ limit: 500 });
      return res.data;
    },
    staleTime: STALE_MS,
    refetchInterval: REFETCH_MS,
    retry: 1,
  });

  const { data: supplierData } = useQuery({
    queryKey: ['nav-escalation-suppliers'],
    queryFn: async () => {
      const res = await suppliersAPI.getAll({ limit: 500 });
      return res.data;
    },
    staleTime: STALE_MS,
    refetchInterval: REFETCH_MS,
    retry: 1,
  });

  return useMemo(() => {
    const products = productData?.products ?? [];
    const pos = poData?.purchaseOrders ?? poData?.data ?? [];
    const orders = orderData?.orders ?? [];
    const supplierList = supplierData?.data ?? supplierData?.suppliers ?? [];

    // Build supplier → PO map (re-use pos already fetched above)
    const posBySupplierId = new Map();
    for (const po of pos) {
      const sid = po.supplier?._id?.toString() ?? po.supplier?.toString();
      if (!sid) continue;
      if (!posBySupplierId.has(sid)) posBySupplierId.set(sid, []);
      posBySupplierId.get(sid).push(po);
    }

    // Single-pass classification: compute OI result once per item, build both count and item list
    const escalatedProducts = products.flatMap(p => {
      const risk = computeSkuRisk(p);
      if (!ESCALATED_SKU.has(risk.level)) return [];
      return [{ domain: 'inventory', entityId: String(p._id), entityRef: p.name || p.sku || '', level: risk.level, label: risk.label, sub: risk.sub ?? null, actionUrl: `/inventory/products/${p._id}` }];
    });

    const escalatedPOs = pos.flatMap(po => {
      const esc = computePOEscalation(po);
      if (!ESCALATED_PO.has(esc.level)) return [];
      return [{ domain: 'purchaseOrders', entityId: String(po._id), entityRef: po.poNumber || String(po._id), level: esc.level, label: esc.label, sub: esc.sub ?? null, actionUrl: `/purchase-orders/${po._id}` }];
    });

    const escalatedOrders = orders.flatMap(o => {
      const ful = computeOrderFulfillment(o);
      if (!ESCALATED_ORDER.has(ful.level)) return [];
      return [{ domain: 'orders', entityId: String(o._id), entityRef: o.orderNumber || String(o._id), level: ful.level, label: ful.label, sub: ful.sub ?? null, actionUrl: `/orders/${o._id}` }];
    });

    const escalatedSuppliers = supplierList.flatMap(s => {
      const risk = computeSupplierRisk(s, posBySupplierId.get(s._id?.toString()) || []);
      if (!ESCALATED_SUPPLIER.has(risk.level)) return [];
      return [{ domain: 'suppliers', entityId: String(s._id), entityRef: s.name || String(s._id), level: risk.level, label: risk.label, sub: risk.sub ?? null, actionUrl: `/suppliers/${s._id}` }];
    });

    return {
      products: escalatedProducts.length,
      purchaseOrders: escalatedPOs.length,
      orders: escalatedOrders.length,
      suppliers: escalatedSuppliers.length,
      // Full items list for alert dispatch — consumers that only read integer fields remain unaffected
      escalatedItems: [...escalatedProducts, ...escalatedPOs, ...escalatedOrders, ...escalatedSuppliers],
    };
  }, [productData, poData, orderData, supplierData]);
}
